import type { ApiClient } from 'clients/yogi-api'
import type {
  AgentInsightAgent,
  AgentInsightDetail,
  AgentInsightFeedPage,
} from 'features/trend-detection/features/insight-agent/types'
import type { CitationFiltersResponse, TableFiltersResponse } from 'src/types'

const INSIGHT_AGENT_URL = '/insight-agent'

/**
 * API surface for generic insight-agent insights.
 *
 * The two provenance calls answer with Ask Yogi's own `CitationFiltersResponse`
 * / `TableFiltersResponse` shapes on purpose: the client's filter-chip
 * components are typed against those generated schemas, so reusing them lets
 * the detail page render provenance with existing components.
 */
export class AgentInsightService {
  constructor(private client: ApiClient) {}

  async getInsight(
    projectId: string,
    insightUuid: string,
  ): Promise<AgentInsightDetail> {
    const { data } = await this.client.get(
      `${INSIGHT_AGENT_URL}/insights/${insightUuid}`,
      { params: { proj_uuid: projectId } },
    )
    return data as AgentInsightDetail
  }

  async listAgents(projectId: string): Promise<AgentInsightAgent[]> {
    const { data } = await this.client.get(`${INSIGHT_AGENT_URL}/agents`, {
      params: { proj_uuid: projectId },
    })
    return (data ?? []) as AgentInsightAgent[]
  }

  async getAgent(
    projectId: string,
    agentUuid: string,
  ): Promise<AgentInsightAgent> {
    const { data } = await this.client.get(
      `${INSIGHT_AGENT_URL}/agents/${agentUuid}`,
      { params: { proj_uuid: projectId } },
    )
    return data as AgentInsightAgent
  }

  async createAgent(
    projectId: string,
    name: string,
    prompt: string,
  ): Promise<AgentInsightAgent> {
    const { data } = await this.client.post(
      `${INSIGHT_AGENT_URL}/agents`,
      { name, prompt },
      { params: { proj_uuid: projectId } },
    )
    return data as AgentInsightAgent
  }

  async updateAgent(
    projectId: string,
    agentUuid: string,
    name: string,
    prompt: string,
  ): Promise<AgentInsightAgent> {
    const { data } = await this.client.put(
      `${INSIGHT_AGENT_URL}/agents/${agentUuid}`,
      { name, prompt },
      { params: { proj_uuid: projectId } },
    )
    return data as AgentInsightAgent
  }

  /**
   * Soft-deletes an agent so it stops running.
   *
   * The agent's existing insights stay on the feed — deletion deactivates the
   * agent rather than removing it, because runs and insights cascade from the
   * agent row and a hard delete would take the project's report history with
   * it.
   */
  async deleteAgent(projectId: string, agentUuid: string): Promise<void> {
    await this.client.delete(`${INSIGHT_AGENT_URL}/agents/${agentUuid}`, {
      params: { proj_uuid: projectId },
    })
  }

  /**
   * Mute or unmute this agent for the CALLING user.
   *
   * Per-user and feed-only: the agent keeps running, other collaborators are
   * unaffected, and a direct link to its report still opens.
   */
  async setEnabled(
    projectId: string,
    agentUuid: string,
    isEnabled: boolean,
  ): Promise<void> {
    await this.client.put(
      `${INSIGHT_AGENT_URL}/agents/${agentUuid}/enabled`,
      { is_enabled: isEnabled },
      { params: { proj_uuid: projectId } },
    )
  }

  /** Dispatches a background run; the caller polls the returned run uuid. */
  async runAgent(
    projectId: string,
    agentUuid: string,
  ): Promise<{ run_uuid: string; status: string }> {
    const { data } = await this.client.post(
      `${INSIGHT_AGENT_URL}/agents/${agentUuid}/run`,
      {},
      { params: { proj_uuid: projectId } },
    )
    return data as { run_uuid: string; status: string }
  }

  /** `agentUuid` narrows the feed to one agent (the agent detail page). */
  async listInsights(
    projectId: string,
    page: number,
    limit: number,
    agentUuid?: string,
  ): Promise<AgentInsightFeedPage> {
    const { data } = await this.client.get(`${INSIGHT_AGENT_URL}/insights`, {
      params: {
        proj_uuid: projectId,
        page,
        limit,
        ...(agentUuid ? { agent_uuid: agentUuid } : {}),
      },
    })
    return data as AgentInsightFeedPage
  }

  async getCitationFilters(
    projectId: string,
    insightUuid: string,
    recordIds: string[],
  ): Promise<CitationFiltersResponse> {
    const { data } = await this.client.post(
      `${INSIGHT_AGENT_URL}/insights/${insightUuid}/citation-filters`,
      {
        record_ids: recordIds
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id)),
      },
      { params: { proj_uuid: projectId } },
    )
    return data as CitationFiltersResponse
  }

  async getTableFilters(
    projectId: string,
    insightUuid: string,
    resultIds: string[],
  ): Promise<TableFiltersResponse> {
    const { data } = await this.client.post(
      `${INSIGHT_AGENT_URL}/insights/${insightUuid}/table-filters`,
      { result_ids: resultIds },
      { params: { proj_uuid: projectId } },
    )
    return data as TableFiltersResponse
  }
}
